import dayjs, { Dayjs } from "dayjs";
export { formatLearningProgressDateValue } from "@/features/learning-progress/utils/format";

// Convention: multi-select fields holding string[] use plural names.
// Number ranges stay as strings to allow empty controlled inputs.
export type TLearningProgressFilterFormExtended = {
  fullName: string;
  rsoCompanyIds: string[];
  nationalityIds: string[];
  examIds: string[];
  lastLoginDateFrom: Dayjs | null;
  lastLoginDateTo: Dayjs | null;
  learningTimeFrom: string;
  learningTimeTo: string;
  thisWeekLearningTimeFrom: string;
  thisWeekLearningTimeTo: string;
  learnedVocabCountFrom: string;
  learnedVocabCountTo: string;
  curriculumProgresses: string[];
  lastExamScoreFrom: string;
  lastExamScoreTo: string;
  japaneseLanguageLevels: string[];
  mockExamCountFrom: string;
  mockExamCountTo: string;
  latestMockExamScoreFrom: string;
  latestMockExamScoreTo: string;
};

export const defaultLearningProgressFilterValues: TLearningProgressFilterFormExtended = {
  fullName: "",
  rsoCompanyIds: [],
  nationalityIds: [],
  examIds: [],
  lastLoginDateFrom: null,
  lastLoginDateTo: null,
  learningTimeFrom: "",
  learningTimeTo: "",
  thisWeekLearningTimeFrom: "",
  thisWeekLearningTimeTo: "",
  learnedVocabCountFrom: "",
  learnedVocabCountTo: "",
  curriculumProgresses: [],
  lastExamScoreFrom: "",
  lastExamScoreTo: "",
  japaneseLanguageLevels: [],
  mockExamCountFrom: "",
  mockExamCountTo: "",
  latestMockExamScoreFrom: "",
  latestMockExamScoreTo: "",
};

// Multi-select keys — used to migrate legacy scalar saved values into arrays
// and to coerce legacy field names (singular form) from older localStorage entries.
export const LEARNING_PROGRESS_MULTI_SELECT_FILTER_KEYS: (keyof TLearningProgressFilterFormExtended)[] = [
  "rsoCompanyIds",
  "nationalityIds",
  "examIds",
  "japaneseLanguageLevels",
  "curriculumProgresses",
];

// Map legacy singular keys (from earlier session) → current plural keys.
const LEGACY_KEY_RENAMES: Record<string, keyof TLearningProgressFilterFormExtended> = {
  rsoCompanyId: "rsoCompanyIds",
  nationalityId: "nationalityIds",
  examScheduledYearMonth: "examIds",
  examScheduledYearMonths: "examIds",
  japaneseLanguageLevel: "japaneseLanguageLevels",
};

// Re-hydrate Dayjs after localStorage JSON round-trip.
export const normalizeLearningProgressDateFields = (
  filters: Partial<TLearningProgressFilterFormExtended>,
): Partial<TLearningProgressFilterFormExtended> => {
  const result = { ...filters };
  if (result.lastLoginDateFrom && typeof result.lastLoginDateFrom === "string") {
    const day = dayjs(result.lastLoginDateFrom);
    result.lastLoginDateFrom = day.isValid() ? day : null;
  }
  if (result.lastLoginDateTo && typeof result.lastLoginDateTo === "string") {
    const day = dayjs(result.lastLoginDateTo);
    result.lastLoginDateTo = day.isValid() ? day : null;
  }
  return result;
};

// Coerce legacy field shapes (renamed singular keys, scalar values) into the current shape.
export const normalizeLearningProgressLegacyFields = (filters: Partial<Record<string, unknown>>): Partial<TLearningProgressFilterFormExtended> => {
  const result: Partial<Record<string, unknown>> = { ...filters };

  // Step 1: rename any legacy singular key to its current plural form.
  for (const [legacyKey, newKey] of Object.entries(LEGACY_KEY_RENAMES)) {
    if (legacyKey in result && !(newKey in result)) {
      result[newKey] = result[legacyKey];
    }
    delete result[legacyKey];
  }

  // Step 2: coerce scalar values on multi-select keys into single-element arrays.
  for (const key of LEARNING_PROGRESS_MULTI_SELECT_FILTER_KEYS) {
    const value = result[key];
    if (value === undefined) continue;
    if (Array.isArray(value)) continue;
    if (value === "" || value === null) {
      result[key] = [];
    } else {
      result[key] = [String(value)];
    }
  }

  // Step 3: drop any unknown key — covers legacy entries (e.g. removed `vocabularyProgresses`/`practiceProgresses`)
  // as well as any future-removed schema fields and typos. Otherwise they inflate the active-filter badge.
  const known = Object.keys(defaultLearningProgressFilterValues);
  for (const key of Object.keys(result)) {
    if (!known.includes(key)) delete result[key];
  }

  return result as Partial<TLearningProgressFilterFormExtended>;
};

// Field-by-field preview labels used by the save-condition modal (in the same order as the form).
// Date and number ranges are rendered separately via the *_RANGE_FIELDS list below.
export const LEARNING_PROGRESS_FILTER_FIELD_LABELS: { key: keyof TLearningProgressFilterFormExtended; label: string }[] = [
  { key: "fullName", label: "氏名" },
  { key: "rsoCompanyIds", label: "所属機関" },
  { key: "nationalityIds", label: "国籍" },
  { key: "examIds", label: "試験予定" },
  { key: "japaneseLanguageLevels", label: "日本語能力" },
  { key: "curriculumProgresses", label: "学習進捗" },
];

export const LEARNING_PROGRESS_DATE_RANGE_FIELDS = {
  lastLogin: {
    fromKey: "lastLoginDateFrom" as const,
    toKey: "lastLoginDateTo" as const,
    label: "最終ログイン",
  },
};

export const LEARNING_PROGRESS_NUMBER_RANGE_FIELDS = {
  learningTime: {
    fromKey: "learningTimeFrom" as const,
    toKey: "learningTimeTo" as const,
    label: "学習時間（合計）",
  },
  thisWeekLearningTime: {
    fromKey: "thisWeekLearningTimeFrom" as const,
    toKey: "thisWeekLearningTimeTo" as const,
    label: "学習時間（今週）",
  },
  lastExamScore: {
    fromKey: "lastExamScoreFrom" as const,
    toKey: "lastExamScoreTo" as const,
    label: "前回本試験点数",
  },
  mockExamCount: {
    fromKey: "mockExamCountFrom" as const,
    toKey: "mockExamCountTo" as const,
    label: "模試実施回数",
  },
  learnedVocabCount: {
    fromKey: "learnedVocabCountFrom" as const,
    toKey: "learnedVocabCountTo" as const,
    label: "覚えた単語数",
  },
  latestMockExamScore: {
    fromKey: "latestMockExamScoreFrom" as const,
    toKey: "latestMockExamScoreTo" as const,
    label: "直近模試結果",
  },
};

export const isEmptyLearningProgressFilterValue = (value: unknown): boolean => {
  if (value === "" || value === null || value === undefined) return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
};

export const stripEmptyLearningProgressFilterFields = (filters: TLearningProgressFilterFormExtended): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (isEmptyLearningProgressFilterValue(value)) continue;
    result[key] = value;
  }
  return result;
};
